import { useEffect, useRef } from "react";

export default function HeroSection() {
  const lineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = lineRef.current;
    if (!el) return;
    let frame: number;
    let start: number | null = null;
    const animate = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / 1200, 1);
      el.style.opacity = String(progress);
      el.style.transform = `scaleY(${progress})`;
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  const handleScroll = () => {
    const el = document.querySelector("#about");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      className="relative w-full min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: "#0a0a0a" }}
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/c8155f35a65b39690cf01cb93c6372e5.jpg"
          alt="Hero background"
          className="w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/60 via-[#0a0a0a]/30 to-[#0a0a0a]/80" />
      </div>

      {/* Main hero content */}
      <div className="relative z-10 w-full flex flex-col items-center justify-center text-center px-5 sm:px-8 pt-28 pb-12">
        <h1
          className="text-white font-serif leading-none mb-6 sm:mb-8 opacity-0 animate-fade-in"
          style={{
            fontSize: "clamp(28px, 10vw, 96px)",
            letterSpacing: "-0.02em",
            animationDelay: "0.3s",
            animationFillMode: "forwards",
          }}
        >
          GLOBAL CAPITAL.<br />
          <span className="italic text-white/90">STRATEGIC</span><br />
          ACQUISITIONS.
        </h1>

        <p
          className="text-white/55 text-xs sm:text-sm tracking-[1px] sm:tracking-[2px] max-w-[280px] sm:max-w-2xl mb-10 sm:mb-12 leading-relaxed opacity-0 animate-fade-in"
          style={{ animationDelay: "0.7s", animationFillMode: "forwards" }}
        >
          We originate and lead the acquisition of strategically positioned UK businesses —{" "}
          <span className="hidden sm:inline"><br /></span>
          partnering with a select group of international capital.
        </p>

        <div
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full max-w-xs sm:max-w-none sm:w-auto opacity-0 animate-fade-in px-0 mb-16 sm:mb-20"
          style={{ animationDelay: "1.0s", animationFillMode: "forwards" }}
        >
          <a
            href="#services"
            onClick={(e) => { e.preventDefault(); document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" }); }}
            className="px-10 sm:px-14 py-4 border border-[#c9a84c] text-[#c9a84c] text-xs tracking-[4px] sm:tracking-[5px] uppercase hover:bg-[#c9a84c] hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap text-center"
          >
            ENTER
          </a>
          <a
            href="#portfolio"
            onClick={(e) => { e.preventDefault(); document.querySelector("#portfolio")?.scrollIntoView({ behavior: "smooth" }); }}
            className="px-10 sm:px-14 py-4 border border-white/20 text-white/60 text-xs tracking-[3px] sm:tracking-[4px] uppercase hover:border-white/50 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap text-center"
          >
            ACCESS OPPORTUNITIES
          </a>
        </div>
      </div>

      {/* Positioning line — just under hero content */}
      <div
        className="relative z-10 w-full border-t border-white/10 opacity-0 animate-fade-in"
        style={{ animationDelay: "1.3s", animationFillMode: "forwards" }}
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 sm:py-8 flex items-center justify-center gap-4 sm:gap-8">
          <div className="hidden sm:block h-px flex-1 bg-white/10" />
          <p className="text-white/40 text-[10px] sm:text-xs tracking-[3px] sm:tracking-[5px] uppercase text-center">
            Focused on privately held UK businesses across key sectors.
          </p>
          <div className="hidden sm:block h-px flex-1 bg-white/10" />
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={handleScroll}
        className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer group z-10"
        aria-label="Scroll down"
      >
        <div
          ref={lineRef}
          className="w-px h-8 sm:h-12 bg-gradient-to-b from-transparent to-[#c9a84c] origin-top"
          style={{ opacity: 0 }}
        />
        <i className="ri-arrow-down-line text-[#c9a84c] text-sm group-hover:translate-y-1 transition-transform duration-300" />
      </button>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease forwards;
        }
      `}</style>
    </section>
  );
}
